import { Alert, Card, Tag } from "antd";
import { FinanceEntryType } from "../typeDefs";
import { getPersonalApportionmentAmount } from "./financeApportionment";
import { isInGstRegisteredPeriod } from "./taxConfig";

const money = (value: number) => `$${(value || 0).toFixed(2)}`;
const gstPortion = (amount: number) => (amount * 3) / 23;

const getGstAmount = (entry: FinanceEntryType) => {
  if (!isInGstRegisteredPeriod(entry.date) || !entry.gstIncluded || !entry.gstRegistered) return 0;
  const gst = gstPortion(entry.amount);
  if (entry.category === "Expenses" || entry.category === "Assets") {
    const apportionRate = entry.apportionMethod === "None" ? 0 : entry.apportionRate ?? 100;
    return gst * (apportionRate / 100);
  }
  return gst;
};

const Box = ({ title, children, tone = "default" }: { title: string; children: React.ReactNode; tone?: "default" | "warn" | "good" }) => (
  <Card
    size="small"
    title={title}
    style={{
      minHeight: 130,
      background: tone === "warn" ? "rgba(246,179,75,0.08)" : tone === "good" ? "rgba(63,134,0,0.08)" : "rgba(255,255,255,0.03)",
      borderColor: tone === "warn" ? "#F6B34B" : tone === "good" ? "#3f8600" : "#5C6B73",
    }}
  >
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>
  </Card>
);

const Arrow = () => <div style={{ alignSelf: "center", color: "#F6B34B", fontSize: 24 }}>→</div>;

export const FinanceFlowDiagram = ({ data, dateRange }: { data: FinanceEntryType[]; dateRange: number[] }) => {
  const entries = data.filter((item) => !item.isDeleted).filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]);
  const revenue = entries.filter((item) => item.category === "Revenue").reduce((total, item) => total + item.amount, 0);
  const expenses = entries.filter((item) => item.category === "Expenses" && !["Salaries and Wages", "Shareholder Salary"].includes(item.subCategory)).reduce((total, item) => total + item.amount, 0);
  const salaries = entries.filter((item) => item.category === "Expenses" && ["Salaries and Wages", "Shareholder Salary"].includes(item.subCategory)).reduce((total, item) => total + item.amount, 0);
  const assets = entries.filter((item) => item.category === "Assets").reduce((total, item) => total + item.amount, 0);
  const directDrawings = entries.filter((item) => item.category === "Equity" && item.subCategory === "Shareholder Drawings").reduce((total, item) => total + item.amount, 0);
  const apportionedPersonalDrawings = entries.reduce((total, item) => total + getPersonalApportionmentAmount(item), 0);
  const drawings = directDrawings + apportionedPersonalDrawings;
  const personalSpendSalary = entries.filter((item) => item.isPersonalCardSpend || item.yearEndTreatment === "Shareholder Salary").reduce((total, item) => total + item.amount, 0) + apportionedPersonalDrawings;
  const nonBusiness = entries.filter((item) => item.category === "Non-Business").reduce((total, item) => total + item.amount, 0);
  const gstCollected = entries.filter((item) => item.category === "Revenue").reduce((total, item) => total + getGstAmount(item), 0);
  const gstClaimable = entries.filter((item) => item.category === "Expenses" || item.category === "Assets").reduce((total, item) => total + getGstAmount(item), 0);
  const companyProfitBeforeAdjustments = revenue - expenses - salaries;
  const personalTaxableEstimate = salaries + drawings;
  const studentLoanEstimate = Math.max(0, personalTaxableEstimate - 24128) * 0.12;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 24 }}>
      <Alert
        type="info"
        showIcon
        message="Accounting flow map"
        description="This diagram shows how imported and manually entered transactions flow through categories, reclassifications, adjustments, GST, company tax, shareholder salary/drawings, personal tax, and student loan estimates."
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
        <Box title="1. Imported / manual entries">
          <span>{entries.length} active entries in selected period</span>
          <span>Bank CSV/OFX imports keep bank IDs for duplicate checks.</span>
          <span>Manual entries, receipts, and accountant journals can also feed reports.</span>
        </Box>
        <Arrow />
        <Box title="2. Category + subcategory">
          <Tag>Revenue: {money(revenue)}</Tag>
          <Tag>Expenses: {money(expenses)}</Tag>
          <Tag>Assets: {money(assets)}</Tag>
          <Tag>Salaries: {money(salaries)}</Tag>
          <Tag>Equity drawings: {money(drawings)}</Tag>
          <Tag>Non-business: {money(nonBusiness)}</Tag>
        </Box>
        <Arrow />
        <Box title="3. Reclassification / exclusions" tone="warn">
          <span>Non-business entries are visible but excluded from business tax logic.</span>
          <span>Personal company-card spend can be reclassified to Equity / Shareholder Drawings.</span>
          <span>Split/apportioned business-account spend contributes its personal portion to shareholder drawings.</span>
          <span>Split spend paid from a personal account records only the business claim; no personal drawing is created.</span>
          <span>Year-end treatment can convert drawings/personal spend to shareholder salary.</span>
          <Tag>Personal apportionment drawings: {money(apportionedPersonalDrawings)}</Tag>
          <Tag>Year-end salary flagged: {money(personalSpendSalary)}</Tag>
        </Box>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
        <Box title="GST return pathway">
          <span>Only entries from GST registration date are counted.</span>
          <span>Revenue GST collected: {money(gstCollected)}</span>
          <span>Expense/asset GST claimable: {money(gstClaimable)}</span>
          <Tag color={gstCollected - gstClaimable >= 0 ? "red" : "green"}>{gstCollected - gstClaimable >= 0 ? "GST to pay" : "GST refundable"}: {money(Math.abs(gstCollected - gstClaimable))}</Tag>
        </Box>
        <Arrow />
        <Box title="Company tax pathway">
          <span>Revenue less operating expenses and salaries produces draft profit.</span>
          <span>Assets usually move through depreciation/accountant adjustments.</span>
          <span>Loss carry-forwards and final adjustments are handled in Tax Carry-Overs / Accountant Alignment.</span>
          <Tag>Draft profit before adjustments: {money(companyProfitBeforeAdjustments)}</Tag>
        </Box>
        <Arrow />
        <Box title="Personal tax pathway" tone="good">
          <span>Shareholder salary + taxable drawings + personal apportionment portions feed personal income.</span>
          <span>PAYE/tax paid, interest/RWT, and accountant salary allocation adjust the final return.</span>
          <Tag>Salary + drawings estimate: {money(personalTaxableEstimate)}</Tag>
          <Tag>Student loan estimate: {money(studentLoanEstimate)}</Tag>
        </Box>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
        <Box title="Bank reconciliation">
          <span>Main-bank entries produce expected bank balance movement.</span>
          <span>Cash/other-account/non-business entries can be excluded from main bank reconciliation.</span>
        </Box>
        <Box title="Accountant Alignment">
          <span>Compares app figures to signed IR4/GST/personal tax targets.</span>
          <span>Use variances to decide reclassification, missing entries, depreciation, and year-end journals.</span>
        </Box>
        <Box title="Final reports">
          <span>Company IR4: business income, losses, shareholder salary/current account.</span>
          <span>GST: sales, GST collected, purchases, GST credits, GST to pay/refund.</span>
          <span>Personal: salary/drawings, income tax, student loan, provisional tax.</span>
        </Box>
      </div>
    </div>
  );
};
